import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { Home, CreditCard, Tv, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";

const CONFIG = {
  INR: {
    symbol: "₹",
    locale: "en-IN",
    code: "INR",
    income: { default: 80000, min: 10000, max: 500000, step: 5000 },
    rent: { default: 25000, min: 0, max: 200000, step: 1000, presets: [15000, 25000, 40000] },
    emis: { default: 12000, min: 0, max: 150000, step: 1000, presets: [5000, 12000, 25000] },
    subs: { default: 5000, min: 0, max: 50000, step: 500, presets: [2000, 5000, 10000] },
  },
  USD: {
    symbol: "$",
    locale: "en-US",
    code: "USD",
    income: { default: 6000, min: 1000, max: 40000, step: 250 },
    rent: { default: 1800, min: 0, max: 15000, step: 100, presets: [1000, 1800, 2800] },
    emis: { default: 700, min: 0, max: 10000, step: 100, presets: [300, 700, 1500] },
    subs: { default: 150, min: 0, max: 3000, step: 25, presets: [50, 150, 400] },
  },
};

const defaultsFor = (cur) => ({
  income: CONFIG[cur].income.default,
  rent: CONFIG[cur].rent.default,
  emis: CONFIG[cur].emis.default,
  subs: CONFIG[cur].subs.default,
});

function SliderRow({ icon: Icon, label, testid, value, cfg, fmt, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-2 text-sm text-slatey">
          <span className="h-7 w-7 rounded-lg bg-secondary flex items-center justify-center shrink-0">
            <Icon size={14} strokeWidth={1.5} className="text-navy" />
          </span>
          {label}
        </span>
        <span className="font-medium text-ink tnum text-sm" data-testid={`${testid}-value`}>{fmt(value)}</span>
      </div>
      <Slider
        value={[value]} min={cfg.min} max={cfg.max} step={cfg.step}
        onValueChange={(v) => onChange(v[0])}
        className="py-1" data-testid={`${testid}-slider`}
      />
      {cfg.presets && (
        <div className="flex gap-2 mt-2">
          {cfg.presets.map((p) => (
            <button
              key={p} type="button" onClick={() => onChange(p)}
              className={`text-xs px-2.5 py-1 rounded-full transition-colors ${value === p ? "bg-mint-light text-mint font-medium" : "bg-secondary text-slatey hover:bg-border"}`}
              data-testid={`${testid}-preset-${p}`}
            >{fmt(p)}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SafeToSpendWidget() {
  const nav = useNavigate();
  const [cur, setCur] = useState("INR");
  const [vals, setVals] = useState(defaultsFor("INR"));
  const c = CONFIG[cur];

  const fmt = useMemo(() => {
    const nf = new Intl.NumberFormat(c.locale, { style: "currency", currency: c.code, maximumFractionDigits: 0, minimumFractionDigits: 0 });
    return (n) => nf.format(n || 0);
  }, [c]);

  const switchCurrency = (next) => { if (next === cur) return; setCur(next); setVals(defaultsFor(next)); };
  const set = (k) => (v) => setVals((s) => ({ ...s, [k]: v }));

  const committed = vals.rent + vals.emis + vals.subs;
  const ratio = vals.income > 0 ? (committed / vals.income) * 100 : 0;
  const safe = vals.income - committed;
  const ratioTone = ratio < 30 ? "text-mint" : ratio < 50 ? "text-navy" : ratio <= 70 ? "text-calm-pending" : "text-calm-alert";
  const overCommitted = safe < 0;

  return (
    <Card id="safe-to-spend-widget" className="rounded-2xl border border-border shadow-card-lg bg-white overflow-hidden" data-testid="safe-to-spend-widget">
      {/* Header + currency toggle */}
      <div className="flex items-center justify-between px-5 sm:px-6 pt-5">
        <div>
          <p className="text-xs tracking-[0.16em] uppercase font-semibold text-mint">Safe-to-Spend Visibility</p>
          <h3 className="font-heading text-lg font-medium text-navy mt-1">See your Commitment Position</h3>
        </div>
        <div className="flex p-1 rounded-full bg-secondary text-sm" role="group" aria-label="Currency">
          {["INR", "USD"].map((k) => (
            <button
              key={k} type="button" onClick={() => switchCurrency(k)}
              className={`h-8 w-9 rounded-full transition-colors font-medium ${cur === k ? "bg-white text-navy shadow-sm" : "text-slatey"}`}
              data-testid={`currency-toggle-${k}`}
            >{CONFIG[k].symbol}</button>
          ))}
        </div>
      </div>

      {/* Inputs */}
      <div className="px-5 sm:px-6 pt-5 space-y-5">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slatey">Monthly take-home income</span>
            <span className="font-heading text-lg text-navy tnum" data-testid="income-value">{fmt(vals.income)}</span>
          </div>
          <Slider
            value={[vals.income]} min={c.income.min} max={c.income.max} step={c.income.step}
            onValueChange={(v) => set("income")(v[0])} className="py-1" data-testid="income-slider"
          />
        </div>
        <SliderRow icon={Home} label="Rent / Housing" testid="rent" value={vals.rent} cfg={c.rent} fmt={fmt} onChange={set("rent")} />
        <SliderRow icon={CreditCard} label="EMIs & BNPL" testid="emis" value={vals.emis} cfg={c.emis} fmt={fmt} onChange={set("emis")} />
        <SliderRow icon={Tv} label="Subscriptions & bills" testid="subs" value={vals.subs} cfg={c.subs} fmt={fmt} onChange={set("subs")} />
      </div>

      {/* Live outputs */}
      <div className="px-5 sm:px-6 pt-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-border bg-white p-4" data-testid="output-committed">
            <p className="text-xs text-slatey">Committed Amount</p>
            <p className="font-heading text-xl text-navy tnum mt-1" data-testid="committed-value">{fmt(committed)}</p>
          </div>
          <div className="rounded-2xl border border-border bg-white p-4" data-testid="output-ratio">
            <p className="text-xs text-slatey">Commitment Ratio</p>
            <p className={`font-heading text-xl tnum mt-1 ${ratioTone}`} data-testid="ratio-value">{ratio.toFixed(1)}%</p>
          </div>
        </div>
        {/* Safe-to-Spend hero card */}
        <div className="relative overflow-hidden rounded-2xl bg-navy text-white border-2 border-mint ring-4 ring-mint/10 p-5 mt-3 shadow-card-md" data-testid="output-safe">
          <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-mint/10" />
          <div className="relative">
            <p className="text-xs text-white/60 flex items-center gap-1.5"><ShieldCheck size={13} className="text-mint" />Estimated Safe-to-Spend</p>
            <p className={`font-heading text-4xl font-light tnum mt-1.5 ${overCommitted ? "text-calm-pending" : "text-mint"}`} data-testid="safe-value">{fmt(safe)}</p>
            <p className="text-xs text-white/55 mt-1.5">
              {overCommitted ? "Your commitments exceed your income — worth reviewing." : "What remains after your recurring commitments this month."}
            </p>
          </div>
        </div>
      </div>

      {/* Conversion banner */}
      <div className="mt-6 bg-mint-light/50 border-t border-mint/20 px-5 sm:px-6 py-5">
        <p className="text-sm text-navy flex items-start gap-2">
          <Sparkles size={16} className="text-mint mt-0.5 shrink-0" />
          Want to track renewal dates, bank mandates, and upcoming alerts? Save your baseline position and get started.
        </p>
        <div className="mt-4">
          <Button onClick={() => nav("/register")} className="w-full rounded-full bg-navy text-white hover:bg-navy-light" data-testid="widget-get-started">
            Save My Baseline & Sign Up Free <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
        <p className="text-[11px] text-slatey mt-3">These are your private estimates. Nothing is saved until you create an account — and you can delete your data anytime with one click.</p>
      </div>
    </Card>
  );
}
